import type { ScaleLinear, ScaleTime } from 'd3-scale'
import { stack, stackOffsetDiverging } from 'd3-shape'
import { useId, useMemo } from 'react'

import type { AreaGraphElement } from '@/features/bidding/components/cascading/charts/types'

type ExclusionOverlayProps = {
  data: AreaGraphElement[]
  layerKeys: string[]
  timeScale: ScaleTime<number, number>
  valueScale: ScaleLinear<number, number>
  excludedSegments: Set<string>
  datetimes: Date[]
  /** Bar padding ratio (0-1) to match AreaGraph's barPadding */
  barPadding?: number
}

/**
 * SVG overlay that renders diagonal-striped rectangles over excluded chart segments,
 * visually greying out the underlying bar.
 */
function ExclusionOverlay({
  data,
  layerKeys,
  timeScale,
  valueScale,
  excludedSegments,
  datetimes,
  barPadding = 0,
}: Readonly<ExclusionOverlayProps>) {
  const patternId = useId() + '-exclusion-stripe'

  const rects = useMemo(() => {
    if (excludedSegments.size === 0 || data.length === 0) return []

    const stackGenerator = stack<AreaGraphElement>()
      .offset(stackOffsetDiverging)
      .value((d, key) => d.layerData[key] ?? 0)
      .keys(layerKeys)

    const stackedData = stackGenerator(data)

    const result: Array<{ key: string; x: number; y: number; width: number; height: number }> = []

    for (let layerIdx = 0; layerIdx < stackedData.length; layerIdx++) {
      const layer = stackedData[layerIdx]
      const layerKey = layerKeys[layerIdx]

      for (let hourIdx = 0; hourIdx < layer.length; hourIdx++) {
        const segmentKey = `${layerKey}:${hourIdx}`
        if (!excludedSegments.has(segmentKey)) continue

        const point = layer[hourIdx]
        if (!point) continue

        const y0 = valueScale(point[0])
        const y1 = valueScale(point[1])

        const rawBarX = timeScale(datetimes[hourIdx])
        const interval = datetimes.length >= 2 ? datetimes[1].getTime() - datetimes[0].getTime() : 3_600_000
        const nextDt = datetimes[hourIdx + 1] ?? new Date(datetimes[hourIdx].getTime() + interval)
        const rawBarWidth = timeScale(nextDt) - rawBarX

        // Inset by barPadding to match the visible colored bar
        const paddingPx = rawBarWidth * barPadding
        const barX = rawBarX + paddingPx / 2
        const barWidth = rawBarWidth - paddingPx

        // Inset vertically by 0.5px on each side to preserve the white stroke
        // separators between stacked segments
        const STROKE_INSET = 0.5
        const top = Math.min(y0, y1) + STROKE_INSET
        const height = Math.max(Math.abs(y1 - y0) - STROKE_INSET * 2, 0)

        result.push({
          key: segmentKey,
          x: barX,
          y: top,
          width: barWidth,
          height,
        })
      }
    }

    return result
  }, [data, layerKeys, timeScale, valueScale, excludedSegments, datetimes])

  if (rects.length === 0) return null

  return (
    <g className="exclusion-overlay" style={{ pointerEvents: 'none' }}>
      <defs>
        <pattern height="6" id={patternId} patternTransform="rotate(45)" patternUnits="userSpaceOnUse" width="6">
          <rect fill="#e5e7eb" height="6" width="6" />
          <line stroke="#9ca3af" strokeWidth="1.5" x1="0" x2="0" y1="0" y2="6" />
        </pattern>
      </defs>
      {rects.map((rect) => (
        <g key={rect.key}>
          {/* White wash to grey out the underlying bar color */}
          <rect fill="white" height={rect.height} opacity={0.7} width={rect.width} x={rect.x} y={rect.y} />
          {/* Diagonal stripe pattern */}
          <rect
            fill={`url(#${patternId})`}
            height={rect.height}
            opacity={0.85}
            width={rect.width}
            x={rect.x}
            y={rect.y}
          />
        </g>
      ))}
    </g>
  )
}

export default ExclusionOverlay
